import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detects that a newer build has been deployed while this tab stayed open.
 *
 * Why: two days after v4.81.60 shipped, 248 unique users were still erroring on
 * v4.81.57, roughly two thirds of everyone hitting a bug, because a tab that is
 * never reloaded keeps running the JavaScript it booted with.
 *
 * This hook only answers the question. What to do about it belongs to the
 * caller: `UpgradeManager` reloads at a moment nobody notices, and
 * `StaleBuildPrompt` — throttled on its own — asks when that isn't possible.
 * Detection itself is never throttled beyond the request floor, because a tab
 * that just upgraded must notice the next deploy too.
 *
 * Detection compares the build id this tab booted with — `VITE_RELEASE`,
 * inlined into the bundle — against `/version.json`, which the deploy writes
 * from the same value (scripts/version-json-plugin.mjs) and serves `no-cache`,
 * invalidating it on CloudFront before anything downstream runs. A ~100-byte
 * probe with an exact answer; the earlier `index.html` entry-script comparison
 * missed deploys that only changed lazy chunks.
 *
 * Every failure path here is deliberately silent — bad JSON, an offline tab, a
 * cached response, a build with no id all mean "cannot tell", never "assume
 * stale". The worst realistic outcome is one unnecessary refresh prompt, which
 * is why this is a better fit than a service worker whose worst case is
 * pinning users to a build they cannot escape.
 */

export const VERSION_PATH = '/version.json';

// Hourly is enough: navigation and tab-visible are the triggers that matter
// for a tab in use, and this only catches the one left open and untouched.
const POLL_INTERVAL_MS = 60 * 60 * 1000;
// Proportional so the spread scales with the interval, and so a deploy doesn't
// have every open tab probing in lockstep.
const JITTER_RATIO = 0.2;

// Floor between checks for the interval and tab-visible triggers. Without it,
// alt-tabbing fires a request per tab switch — the interval is not the cost,
// the focus events are.
const MIN_CHECK_GAP_MS = 15 * 60 * 1000;

// Navigation gets its own, much shorter floor. A click is a deliberate act at
// a moment the tab can upgrade unnoticed, and holding it to the tab-switch
// floor meant one background check could suppress every navigation for the
// next quarter of an hour — a deploy went unnoticed for 15 minutes of active
// use. At ~100 bytes a request, once a minute costs nothing.
const NAVIGATION_CHECK_GAP_MS = 60 * 1000;

/**
 * The id the server reports, or null for anything that is not a plain
 * non-empty string — a half-written file, an error page served with a 200, a
 * proxy that rewrote the body. Null means "cannot tell".
 */
export const buildIdFrom = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;
  const id = (payload as { buildId?: unknown }).buildId;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

export interface StaleBuildCheck {
  /** A newer build is live. Sticks until the tab reloads onto it. */
  isStale: boolean;
  /** The build id the server last reported as live, once it differed. */
  latestBuildId: string | null;
  /** When this tab first learned it was stale, or null. */
  staleSince: number | null;
  /** Reload onto the new build. */
  reload: () => void;
}

export interface StaleBuildCheckOptions {
  enabled?: boolean;
  intervalMs?: number;
  /**
   * Which page the reader is on; a change triggers a check. The caller passes
   * a route, not a pathname — a page that routes its own tabs changes the
   * pathname without anyone leaving it. Passed in rather than read with
   * `useLocation` so the hook has no opinion about which router — if any —
   * it renders under. Omit to leave navigation out of the triggers.
   */
  pageKey?: string;
}

export const useStaleBuildCheck = ({
  enabled = !import.meta.env.DEV,
  intervalMs = POLL_INTERVAL_MS,
  pageKey,
}: StaleBuildCheckOptions = {}): StaleBuildCheck => {
  const [isStale, setIsStale] = useState(false);
  const [latestBuildId, setLatestBuildId] = useState<string | null>(null);
  const [staleSince, setStaleSince] = useState<number | null>(null);
  // Inlined at build time. Empty in `pnpm start` — and it was empty on every
  // staging build until the workflows resolved it from HEAD — so an empty id
  // must mean "cannot tell", or the tab would read as stale forever.
  const bootedBuildId = import.meta.env.VITE_RELEASE || null;
  const lastCheckedAt = useRef(0);

  const check = useCallback(
    async (minGapMs: number = MIN_CHECK_GAP_MS) => {
      if (!enabled || document.visibilityState !== 'visible') return;
      if (!bootedBuildId) return;
      if (Date.now() - lastCheckedAt.current < minGapMs) return;
      lastCheckedAt.current = Date.now();

      try {
        const res = await fetch(`${window.location.origin}${VERSION_PATH}`, {
          cache: 'no-store',
          credentials: 'omit',
        });
        if (!res.ok) return;
        const served = buildIdFrom(await res.json());
        // Cannot tell, or unchanged — either way, say nothing.
        if (!served || served === bootedBuildId) return;
        // Keep polling once stale: a second deploy moves `latestBuildId` on so
        // the warm-up targets the build the reload will actually land on.
        setLatestBuildId(served);
        setStaleSince((at) => at ?? Date.now());
        setIsStale(true);
      } catch {
        // Offline or blocked: staying quiet is correct, this is advisory only.
      }
    },
    [enabled, bootedBuildId]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const timer = window.setInterval(
      () => check(),
      intervalMs * (1 + Math.random() * JITTER_RATIO)
    );
    // Returning to a tab left open overnight is the best moment to look.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check, enabled, intervalMs]);

  // Moving to another page is when someone is actually using the tab, and —
  // once the reload is automated — the moment it can happen unnoticed. The
  // caller passes a route: a drawer, a filter or one of the page's own tabs
  // is not a page boundary.
  const lastPageKey = useRef(pageKey);
  useEffect(() => {
    if (pageKey === undefined || pageKey === lastPageKey.current) return;
    lastPageKey.current = pageKey;
    check(NAVIGATION_CHECK_GAP_MS);
  }, [pageKey, check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { isStale, latestBuildId, staleSince, reload };
};
